import type { ProjectCopyMap } from './types';

export const ja: ProjectCopyMap = {
  kura: {
    tagline: '個人向けエージェント OS — ローカルのデーモン 1 つと、複数の薄いクライアント。',
    summary:
      'Rust 製のコントロールプレーンがローカルで動き、ランタイム、LLM プロバイダー、チャネルコネクター、ストレージ、イベントを担います。クライアントは薄いまま：React 製 Web UI、フルスクリーンの Rust TUI、チャットチャネルコネクター、TypeScript SDK — すべてが同じ JSON Schema の契約に従います。',
    body: [
      'プロダクトの本体はデーモンです。正しさが求められるもの — セッション状態、プロバイダーのルーティング、ツールハーネス、イベントログ — はすべて 1 つの Rust ワークスペースに収まり、ローカル HTTP API 越しに公開されます。クライアントはその上のビューにすぎません。デーモンのバイナリは `dope-cli`、HTTP API は `dope-api` です。',
      '言語をまたぐ契約は `schemas/` に JSON Schema として置かれ、API・イベント・設定の唯一の情報源になります。Rust デーモンと TypeScript クライアントは同じ定義から生成され、慣習で手作業に同期させることはありません。',
      '明示された前提として、長寿命のエージェント状態は観測可能・再生可能で、安全に進化できなければなりません。コンテキスト設計、記憶、計画、引き継ぎ、ポリシーは小手先の修正ではなく再設計の対象です。',
    ],
    highlights: [
      'Rust のコントロールプレーン：ランタイム、LLM プロバイダー、チャネル／コネクター、ストア、イベント、HTTP API、ハーネスを 1 つのワークスペースに集約。',
      '1 つのデーモンに対する 3 つの入口：React 19 + Vite の Web UI、フルスクリーンの Rust TUI（`dope-tui`）、チャットチャネルコネクター。',
      '共有の JSON Schema 契約から生成される TypeScript クライアント SDK（`@dope/client`）。',
      'テスト環境と本番環境を分離 — `~/.dope-test` はポート 19192、`~/.dope` は 19191。テストでは実際のコネクターは既定で無効。',
      '以前の Go 製デーモンは Rust ワークスペースに完全に置き換えられ、その記録は `crates/MIGRATION.md` にあります。',
    ],
  },

  gozen: {
    tagline: '複数 CLI の環境切り替えツール。API プロキシの自動フェイルオーバー付き。',
    summary:
      'Claude Code、Codex、OpenCode の環境を 1 か所で切り替え、上流プロバイダーが不安定になっても作業を止めません。GoZen が API トラフィックをプロキシし、自動でフェイルオーバーします。',
    body: [
      'API 設定はすべて `~/.zen/zen.json` に集約され、単一の `zend` デーモンがプロキシサーバーとパスワード保護付き Web UI の両方をホストします。ディレクトリを特定のプロファイルと CLI に紐づけられるため、プロジェクトフォルダーで `zen` と打つだけで、正しいクライアントが正しいプロバイダーに接続します。',
      '導入する理由はプロキシにあります。リクエストは thinking、image、long-context などの特性でルーティングされ、主系が使えないときはバックアップのプロバイダーへ切り替わります。v3 ではプロバイダー・モデル・プロジェクト別のトークン／コスト集計、警告・降格・遮断を伴う予算上限、プロバイダーのヘルスチェック、複数の負荷分散戦略、Webhook 通知が加わりました。',
      '設定は WebDAV、S3、GitHub Gist、GitHub リポジトリ経由で端末間同期でき、AES-256-GCM で暗号化されます。Web UI のトークンはセッション認証の下で RSA 暗号化して送られます。',
    ],
    highlights: [
      'Claude Code、Codex、OpenCode を 1 つのツールで切り替え。ディレクトリ紐づけでプロジェクト単位の設定が可能。',
      'バックアップへの自動フェイルオーバーを備えた HTTP プロキシと、シナリオ別ルーティング（thinking、image、長いコンテキスト）。',
      'プロバイダー・モデル・プロジェクト別の利用量とコスト集計。日／週／月の予算上限に対応。',
      'レイテンシとエラー率を追うヘルス監視。フェイルオーバー、ラウンドロビン、最小レイテンシ、最小コストの分散戦略。',
      'WebDAV、S3、GitHub Gist、GitHub リポジトリでの暗号化済み設定同期（AES-256-GCM）。',
      'Telegram、Discord、Slack、Lark、Messenger から Claude Code のセッションを監視・操作できる Bot ゲートウェイ。',
      '`zen upgrade` による自己更新と、zsh / bash / fish の補完。',
    ],
    commandLabels: ['インストール', '最初の実行'],
  },

  loopforge: {
    tagline: 'デスクトップワークベンチを備えた、独立したローカルのゲーム開発エージェント。',
    summary:
      'Loopforge は普通のゲームリポジトリの中で動きます。アイデアを遊べる実験に変え、技術的な証拠と人による証拠を集め、続けるか・やめるか・作り直すかを明示的に決める手助けをします。制御の中心はエージェントで、デスクトップの Workbench がその窓口です。CLI と Skills は内部の実行手段であり、手作業で操作するものではありません。',
    body: [
      'プロジェクトを開くこと自体が準備のすべてです。Workbench がネイティブのフォルダー選択でゲームリポジトリを受け取り、そのプロジェクトのエージェントを起動または再接続し、限定されたプロジェクト文脈を読み込みます。その過程でプロバイダーの資格情報、環境変数、アクセストークンが露出することはありません。',
      'インターフェースはエージェントのプロセスではなくプロジェクトを軸に構成されています。プロジェクトメニューでリポジトリとプロジェクト単位のビューを切り替え、ヘッダーがプロジェクトの身元と必要な操作を担い、作業領域にはモードごとの道具が並び、フローティングのモードツールバーが探索・設計・ビルド・テストを行き来します。チャットは作業の隣にあり、作業を置き換えません。プロジェクトの状態と証拠はゲームリポジトリに残るため、何が起きたかの記録はチャット履歴だけではないからです。',
      '境界は意図的に引かれています。`apps/agent` はドメインのエージェントとユーザーに見える振る舞いを持ち、`cli` は決定論的なプロジェクト操作とヘッドレスのアダプターを持ちますが、明確に制御の中心ではありません。`skills` は文脈判断が要る作業のためのバージョン付き能力、`contracts` はバージョン付きのスキーマです。Kura は汎用のモデル・セッション・ランタイムを提供し、Loopforge の状態は一切持ちません。リリース版アプリはエージェントとピン留めした Kura サイドカーを同梱します。',
    ],
    highlights: [
      'デスクトップの Workbench（Tauri + React）が製品の本体で、抱えるサイドカーはエージェントだけ。',
      'プロジェクトは普通のゲームリポジトリから開き、読み込まれる文脈に資格情報や環境変数は含まれません。',
      '探索・設計・ビルド・テストを切り替えるモードツールバー。エージェントのチャットは作業の隣に置かれます。',
      '状態と証拠はリポジトリに書き込まれるので、何が起きたかの記録がチャットログ頼みになりません。',
      '決定論的な操作とバージョン付き Skills は自動化と診断のために残されています。内部の能力であり、入口ではありません。',
      '汎用のモデル・セッション・ランタイムはピン留めした Kura サイドカーが供給し、ドメインの論理は Loopforge 側に留まります。',
    ],
    commandLabels: ['チェックアウトから Workbench を起動'],
    requirements:
      'Git、Node.js 22 と pnpm、Rust と Cargo、Python 3.11+ と uv。Godot 4 は Godot ワークフローを使うときのみ必要です。パッケージ済みのリリースはまだないため、デスクトップアプリはチェックアウトからビルドします。',
  },

  pingo: {
    tagline: 'ゼロから設計した Web Canvas レンダリングエンジン。',
    summary:
      '高性能な TSX ランタイムと決定論的な Rust/WASM コア、ネイティブの仮想スクロールと Canvas ネイティブのテキスト編集、そして SCSS/Less のビルド時パイプラインを備えたバージョン管理の CSS サブセット。shadcn に沿った UI コンポーネントライブラリ（約 50 コンポーネント）を同梱し、公式サイトのコンポーネントドキュメントはすべてエンジン自身が Canvas 上にリアルタイムで描画しています。',
    body: [
      'Pingo はマイルストーンロードマップの初期段階を越えました。編集・イベント・ヒットテスト・アクセシビリティの主系統はすでに稼働しており、テキスト・画像・図形などの基本要素、ネイティブの仮想スクロール、IME 対応の Canvas ネイティブなテキスト編集という実用的なプリミティブを備えています。技術判断は引き続き `docs/design.md` が基準です。',
      'スタイリングは、カスケードの意味論が予測可能なバージョン管理の CSS サブセットで、SCSS/Less のビルド時パイプラインにより既存のスタイルシートをそのまま持ち込めます。その上に shadcn に沿った約 50 の UI コンポーネント — ボタン、フォーム、オーバーレイ、データ表示 — が揃い、すべて DOM ではなく Canvas 上に描画されます。',
      '公式サイト自体が証拠です。すべてのコンポーネントドキュメントに、エンジン自身がリアルタイムで描画するライブデモが埋め込まれ、ブラウザ上でそのまま操作できます。ドキュメントに見えるものが、そのまま Pingo 上で動いているものです。',
    ],
    highlights: [
      '仮想スクロールをアドオンではなく第一級のプリミティブとして備えた、高性能な TSX ランタイム。',
      'バージョン管理されたバイナリ ABI の背後にある、決定論的な Rust/WASM コア。',
      'Canvas ネイティブのテキスト編集：EditContext 優先、集中管理された textarea プロキシへのフォールバック、決定論的な IME の記録と再生。',
      'プラットフォーム認定はマイルストーン完了と明確に切り離されており、未認定の環境では性能を主張しません。',
    ],
    commandLabels: ['リポジトリのチェックアウトからプローブを実行'],
    requirements: 'Node.js 22.12+、pnpm 10.33.2、Rust 1.96.0（wasm32-unknown-unknown ターゲット付き）。',
  },

  deckle: {
    tagline: 'AI が生成した大量の Web 成果物のための無限キャンバス。',
    summary:
      '数百の生きた iframe はスケールせず、すべてを画像に平坦化すると選択やイベントの対象指定が失われます。Deckle は成果物を保持したまま扱います — ソース、永続状態、インタラクションツリー、描画キャッシュ、任意のライブランタイム — そのため Figma のような選択と有効化が残ります。バックエンドに依存しないエンジン契約は実装・テスト済みですが、ブラウザ検証ゲートはまだ通過していません。',
    body: [
      'iframe はブラウジングコンテキスト、DOM/CSS の状態、スクリプトの realm、リソース、描画状態を保持し続けるため、生成ページを数百枚生かしておくのは非常に高くつきます。設計上の答えが保持型の成果物モデル — `成果物 = ソース + 永続状態 + インタラクションツリー + 描画キャッシュ + 任意のライブランタイム` — であり、スナップショットは描画キャッシュにすぎません。ドキュメントとインタラクションのモデルは残り続け、選択、イベントルーティング、有効化、リビジョン安全な復元に使えます。',
      '成果物はエージェントから届くため逐次的に到着します。そこでストリーミングは、ノードモデルを包む層ではなくモデル自身の一級の性質になっています。内容の種類ごとに自前の境界でコミットし — 書記素、1 行、閉じた Markdown 構造、JSON の値、確定した HTML タグ — その境界は前にしか進みません。読み手が解釈の撤回を目にすることはありません。',
      '現時点で実装済み・テスト済みなのは、シーントランザクション、カメラと空間の仮想化、ライフサイクルと予算、成果物のリビジョン、サニタイズ、制御されたランタイムプロトコル、保持型レンダリング、内部ヒットテストです。未了なのは、M0 のブラウザ検証ゲートが未通過であること、実験的な HTML-in-Canvas API への対応はプローブが検出する「能力」であって約束ではないこと、そして実測が出るまで絶対的な性能・メモリのしきい値を設定しないことです。安定した公開契約はまだ 1 つもありません。',
      '名前そのものが主張です。deckle とは手漉き紙で、パルプがまだ落ち着かないうちに紙を縁取る枠のことで、その枠が残す不揃いな縁を deckle edge と呼びます。まだ届き終えていない内容に枠を与え、その境界を隠さず見せる — このエンジンがしているのはまさにそれです。',
    ],
    highlights: [
      '保持型の成果物モデル：スナップショットは描画キャッシュにすぎないため、選択とイベント対象指定が失われません。',
      'ストリーミングがノードモデルに組み込まれ、種類ごとのコミット境界は前進のみ — 撤回される解釈はありません。',
      '実装・テスト済みのエンジン契約：シーントランザクション、カメラと空間の仮想化、ライフサイクル、予算、リビジョン、サニタイズ、ランタイムプロトコル、保持型レンダリング、ヒットテスト。',
      'セキュリティは後付けの強化ではなく第一級のパッケージ — サニタイザー、URL ポリシー、クォータ、ケーパビリティ。',
      'Apache-2.0 を採用。理由は特許許諾で、ストリーミング境界モデルと Canvas ネイティブの描画プロファイルは明示的な許諾が効く実装領域です。',
      'ライブラリは 0.3.0 まで `@dopejs/canvas-*` として公開され、0.4.0 から `@dopejs/deckle-*` に改称されます。変わるのは名前だけです。',
    ],
    requirements: 'Node.js 22.12+ と pnpm 10.33.2。',
  },
};
